export { SYSTEM_TASK_TYPE_KEYS, originTaskType, canCreateTaskManually } from './task-creation';
import { z } from 'zod';
import { BlockDocument } from './doc/registry';
import { RecordInterfaceDefinition } from './layouts';
import { localizedDescription, localizedName, nextText } from './localized-storage';
import { type LocalizedText, languageOf, resolveText } from './localized-text';
import { EMPTY_PLAYBOOK, TaskPlaybook, type TaskNextSteps, type TaskPlaybookDTO } from './task-actions';

export const TaskStatus = z.enum(['open', 'in_progress', 'in_review', 'completed', 'cancelled']);
export type TaskStatus = z.infer<typeof TaskStatus>;
/** The categories a task is in; `status` is the one authoritative category everywhere. */
export const TASK_STATUSES = TaskStatus.options;
export const CLOSED_TASK_STATUSES: readonly TaskStatus[] = ['completed', 'cancelled'];
export const isClosedTask = (status: TaskStatus) => CLOSED_TASK_STATUSES.includes(status);

const KEY = /^[a-z][a-z0-9_]{0,62}$/;
/** Icons a task type may use (the shared icon set; the web client maps them to its `Icon`). */
export const TASK_ICONS = [
  'task',
  'check',
  'pencil',
  'mail',
  'phone',
  'file',
  'gallery',
  'shield',
  'calendar',
  'clock',
  'person',
  'members',
  'branch',
  'search',
  'bolt',
  'sparkle',
  'star',
  'briefcase',
  'comment',
  'alert',
] as const;
export const TaskIcon = z.enum(TASK_ICONS);
export type TaskIcon = z.infer<typeof TaskIcon>;

/** One state of a type's lifecycle; it belongs to one status category. */
export const TaskStateDef = z.object({
  key: z.string().regex(KEY).max(40),
  label: localizedName(60),
  status: TaskStatus,
});
export type RawTaskStateDef = z.infer<typeof TaskStateDef>;
export const OUTCOME_TONES = ['success', 'danger', 'warning', 'neutral'] as const;
/** How a task of a type ends. `note`: whether the person must/may explain the outcome. */
export const TaskOutcomeDef = z.object({
  key: z.string().regex(KEY).max(40),
  label: localizedName(60),
  tone: z.enum(OUTCOME_TONES).default('neutral'),
  status: z.enum(['completed', 'cancelled']).default('completed'),
  note: z.enum(['none', 'optional', 'required']).default('none'),
});
export type RawTaskOutcomeDef = z.infer<typeof TaskOutcomeDef>;

export const SLA_UNITS = ['hours', 'days', 'business_days'] as const;
export const TaskDuration = z.object({
  amount: z.number().int().min(1).max(999),
  unit: z.enum(SLA_UNITS),
});
export type TaskDuration = z.infer<typeof TaskDuration>;
export const TaskEscalation = z.object({
  id: z.string().regex(/^[a-z0-9_-]{1,32}$/),
  after: TaskDuration,
  action: z.enum(['notify', 'raise_priority', 'reassign']),
  /** notify / reassign: the supervisor, a member or everyone with a role. */
  to: z
    .object({
      kind: z.enum(['supervisor', 'member', 'role']),
      id: z.string().max(100).nullable().default(null),
    })
    .default({ kind: 'supervisor', id: null }),
});
export type TaskEscalation = z.infer<typeof TaskEscalation>;
export const TaskSla = z.object({
  due: TaskDuration.nullable().default(null),
  warnAt: z.number().int().min(50).max(95).nullable().default(null),
  escalations: z.array(TaskEscalation).max(5).default([]),
});
export type TaskSla = z.infer<typeof TaskSla>;

export const TaskPool = z.object({
  memberIds: z.array(z.uuid()).max(100).default([]),
  roleKeys: z.array(z.string().max(63)).max(20).default([]),
  teamIds: z.array(z.uuid()).max(20).default([]),
});
export type TaskPool = z.infer<typeof TaskPool>;
const EMPTY_POOL: TaskPool = { memberIds: [], roleKeys: [], teamIds: [] };
export const ASSIGNMENT_STRATEGIES = ['claim', 'round_robin', 'least_loaded'] as const;
export const TaskAssignmentRule = z.object({
  mode: z.enum(['manual', 'creator', 'member', 'pool']).default('manual'),
  memberId: z.uuid().nullable().default(null),
  pool: TaskPool.default(EMPTY_POOL),
  strategy: z.enum(ASSIGNMENT_STRATEGIES).default('claim'),
});
export type TaskAssignmentRule = z.infer<typeof TaskAssignmentRule>;

/** Layouts of a type: the detail page (an interface definition), variants per audience, the Inbox row. */
export const TaskLayoutVariant = z.object({
  id: z.string().regex(/^[a-z0-9_-]{1,32}$/),
  name: localizedName(60),
  roleKeys: z.array(z.string().max(63)).max(20).default([]),
  teamIds: z.array(z.uuid()).max(20).default([]),
  definition: RecordInterfaceDefinition,
});
export type TaskLayoutVariant = z.infer<typeof TaskLayoutVariant>;
export const TaskRowLayout = z.object({
  /** Up to three type or system field keys shown on the Inbox row after the title. */
  fields: z.array(z.string().max(63)).max(3).default([]),
  showRecord: z.boolean().default(true),
  showAssignee: z.boolean().default(false),
});
export type TaskRowLayout = z.infer<typeof TaskRowLayout>;
export const TaskTypeLayouts = z.object({
  detail: RecordInterfaceDefinition.nullable().default(null),
  variants: z.array(TaskLayoutVariant).max(8).default([]),
  row: TaskRowLayout.default({ fields: [], showRecord: true, showAssignee: false }),
});
export type TaskTypeLayouts = z.infer<typeof TaskTypeLayouts>;
/** What a role may not do with a field of a task type (the field-permission model of objects). */
export const TaskFieldRestriction = z.object({
  roleKey: z.string().min(1).max(63),
  access: z.enum(['hidden', 'read_only']),
});
export type TaskFieldRestriction = z.infer<typeof TaskFieldRestriction>;

/**
 * Task fields written before F11b: a fixed list of simple kinds with options and relation settings on the
 * field. They are read as fields of the shared field-type registry (`select` options become
 * `{ key, label }` options, `relation` a `record_ref`, `checkbox` a `boolean` …), so every stored type keeps parsing.
 */
const LEGACY_TASK_FIELD_TYPES: Record<string, string> = {
  textarea: 'text',
  checkbox: 'boolean',
  multiselect: 'multi_select',
  member: 'user',
  money: 'currency',
  relation: 'record_ref',
};
/** An option key from a legacy option text (stored values are mapped the same way, `legacyOptionKey`). */
export function legacyOptionKey(text: string): string {
  const key = text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return key || 'option';
}
function upgradeLegacyTaskField(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const f = raw as Record<string, unknown>;
  const legacyOptions = Array.isArray(f.options) && f.options.every((o) => typeof o === 'string');
  const mapped = typeof f.type === 'string' ? LEGACY_TASK_FIELD_TYPES[f.type] : undefined;
  if (
    !mapped &&
    !(f.config === undefined && (legacyOptions || 'objectKey' in f || 'multiple' in f || 'objectTrait' in f))
  )
    return raw;
  const { options, objectKey, objectTrait, multiple, ...rest } = f;
  const type = mapped ?? f.type;
  const config: Record<string, unknown> = { ...((f.config as Record<string, unknown> | undefined) ?? {}) };
  if (f.type === 'textarea') Object.assign(config, { multiline: true, maxLength: 10000 });
  if (type === 'select' || type === 'multi_select') {
    const seen = new Set<string>();
    config.options = ((legacyOptions ? options : []) as string[]).flatMap((label) => {
      let key = legacyOptionKey(label);
      for (let n = 2; seen.has(key); n++) key = `${legacyOptionKey(label)}_${n}`;
      seen.add(key);
      return [{ key, label }];
    });
  }
  if (type === 'record_ref') {
    if (typeof objectKey === 'string') config.objectKey = objectKey;
    if (objectTrait === 'person') config.trait = 'person';
    config.multiple = multiple === true;
  }
  return { ...rest, type, config };
}

/**
 * One field of a task type. It has the shape of a field of an object (`CreateFieldInput`): a key, a label,
 * a type of the field-type registry with its config, `required`, `readOnly` and a group — and the same
 * special types (formula, rollup, lookup, AI field). The type's config is validated by the engine against the
 * registry (`taskFieldProblems`).
 */
export const TaskField = z.preprocess(
  upgradeLegacyTaskField,
  z.object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
    /** Plain, or one label per language (LocalizedText, plan §8.10). */
    label: localizedName(100),
    description: localizedDescription(500).default(''),
    type: z
      .string()
      .min(1)
      .max(40)
      .regex(/^[a-z][a-z0-9_]*$/),
    config: z.record(z.string(), z.unknown()).default({}),
    required: z.boolean().default(false),
    readOnly: z.boolean().default(false),
    group: z.string().max(80).nullable().default(null),
    /** Field permissions: roles that may not see the field or not change it. */
    restrictions: z.array(TaskFieldRestriction).max(40).default([]),
  }),
);
export type RawTaskFieldInput = z.input<typeof TaskField>;
export const TaskTypeInput = z
  .object({
    key: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/),
    /** Plain, or one name per language (LocalizedText, plan §8.10); likewise description and field labels. */
    name: localizedName(100),
    description: localizedDescription(1000).default(''),
    action: z.enum(['none', 'email', 'call', 'page', 'presentation', 'pdf']).default('none'),
    actionField: z.string().max(63).nullable().default(null),
    /**
     * How a task of an e-mail or call type ends: `action` = only through what it asks for (the e-mail sent from the
     * task, the call logged on it or on its record); `manual` = also by hand. Cancelling stays possible either way.
     */
    completion: z.enum(['manual', 'action']).default('manual'),
    fields: z.array(TaskField).max(50).default([]),
    requiresReview: z.boolean().default(false),
    archived: z.boolean().default(false),
    /** Can a member or the workspace agent create this type without an originating workflow? */
    manualCreation: z.boolean().default(true),
    /** Task platform (F11): appearance, lifecycle, outcomes, SLA, assignment, specialized UI. */
    icon: TaskIcon.default('task'),
    color: z.string().max(40).default('slate'),
    /** The registered specialized UI (`task-ui` extension point); unknown keys fall back to `generic`. */
    ui: z.string().regex(KEY).default('generic'),
    states: z.array(TaskStateDef).max(12).default([]),
    outcomes: z.array(TaskOutcomeDef).max(8).default([]),
    sla: TaskSla.default({ due: null, warnAt: null, escalations: [] }),
    assignment: TaskAssignmentRule.default({
      mode: 'manual',
      memberId: null,
      pool: EMPTY_POOL,
      strategy: 'claim',
    }),
    subtasks: z
      .object({ allow: z.boolean().default(false), requireDone: z.boolean().default(false) })
      .default({
        allow: false,
        requireDone: false,
      }),
    /** Detail layouts (interface builder for tasks); null = the generated default. */
    layout: TaskTypeLayouts.nullable().default(null),
    /** Server-set: owned by an origin (approval, signing, conflicts); cannot be deleted or archived. */
    system: z.boolean().default(false),
    /** Outcome-driven next actions (task-actions, F14): what happens when a task ends, reaches a state or a step is done. */
    playbook: TaskPlaybook.default(EMPTY_PLAYBOOK),
  })
  .superRefine(validateTaskTypeKeys);
function validateTaskTypeKeys(
  x: { fields: Array<{ key: string }>; states: Array<{ key: string }>; outcomes: Array<{ key: string }> },
  ctx: z.RefinementCtx,
) {
  for (const [key, label] of [
    ['fields', 'Field'],
    ['states', 'State'],
    ['outcomes', 'Outcome'],
  ] as const)
    if (new Set(x[key].map((item) => item.key)).size !== x[key].length)
      ctx.addIssue({ code: 'custom', path: [key], message: `${label} keys must be unique` });
}
/** Write boundary: omission preserves manual policy; the normalized stored/reader schema always has a boolean. */
export const TaskTypeWriteInput = z
  .object({ ...TaskTypeInput.shape, manualCreation: z.boolean().optional() })
  .superRefine(validateTaskTypeKeys);
/** A task type as written: names, descriptions and field labels may be LocalizedText. */
export type RawTaskType = z.infer<typeof TaskTypeInput>;
type RawTaskField = RawTaskType['fields'][number];
/** A task type for a reader: every text in the reader's language (`GET /task-types`). */
export type TaskTypeDTO = Omit<
  RawTaskType,
  'name' | 'description' | 'fields' | 'states' | 'outcomes' | 'playbook'
> & {
  playbook: TaskPlaybookDTO;
  /** The detail layout this reader sees (their role's or team's variant, else the type's own); null = generated. */
  viewerLayout?: z.infer<typeof RecordInterfaceDefinition> | null;
  /** What this reader's role may not do with fields of the type (field permissions): by field key. */
  viewerAccess?: Record<string, 'hidden' | 'read_only'>;
  /** At least one task of the type exists (set by `GET /task-types`); filters leave out idle built-in types. */
  used?: boolean;
  name: string;
  description: string;
  fields: Array<Omit<RawTaskField, 'label' | 'description'> & { label: string; description: string }>;
  states: Array<Omit<RawTaskStateDef, 'label'> & { label: string }>;
  outcomes: Array<Omit<RawTaskOutcomeDef, 'label'> & { label: string }>;
};
export const taskHasOutcome = (action: TaskTypeDTO['action']) => action !== 'email';

type OptionLike = { key?: unknown; label?: unknown; i18n?: unknown };
const optionsOf = (config: Record<string, unknown>): OptionLike[] | null =>
  Array.isArray(config.options) ? (config.options as OptionLike[]) : null;
const i18nOf = (o: OptionLike): Record<string, string> | null =>
  o.i18n && typeof o.i18n === 'object' && !Array.isArray(o.i18n) ? (o.i18n as Record<string, string>) : null;

/** The text of a translated option in `locale` (exact → language → English → the stored label). */
export function optionLabelIn(
  o: { label: string; i18n?: Record<string, string> | undefined },
  locale: string,
): string {
  const map = o.i18n;
  if (!map) return o.label;
  return resolveText(map, locale) || o.label;
}

/** Option labels of a choice field in the reader's language (options keep their `i18n` translations). */
function resolveOptionLabels(config: Record<string, unknown>, locale: string): Record<string, unknown> {
  const options = optionsOf(config);
  if (!options?.some((o) => i18nOf(o))) return config;
  return {
    ...config,
    options: options.map((o) =>
      i18nOf(o) && typeof o.label === 'string'
        ? { ...o, label: optionLabelIn({ label: o.label, i18n: i18nOf(o)! }, locale) }
        : o,
    ),
  };
}

/**
 * The options of a saved choice field after a write from the editor, which works on the reader's texts:
 * an option whose text is what the reader reads changes nothing; a changed text of a translated option sets
 * only the reader's language; a plain option is replaced. Translations written explicitly are kept as given.
 */
function mergeOptions(
  current: Record<string, unknown> | undefined,
  next: Record<string, unknown>,
  locale: string,
): Record<string, unknown> {
  const options = optionsOf(next);
  const before = current ? optionsOf(current) : null;
  if (!options || !before) return next;
  const lang = languageOf(locale);
  return {
    ...next,
    options: options.map((o) => {
      const c = before.find((x) => x.key === o.key);
      if (!c || typeof c.label !== 'string' || typeof o.label !== 'string') return o;
      const cm = i18nOf(c);
      if (o.i18n !== undefined && JSON.stringify(o.i18n) !== JSON.stringify(cm)) return o;
      const { i18n: _drop, ...rest } = o;
      if (!cm) return rest;
      if (optionLabelIn({ label: c.label, i18n: cm }, locale) === o.label)
        return { ...rest, label: c.label, i18n: cm };
      const i18n = { ...cm, [lang]: o.label };
      return { ...rest, label: i18n.en ?? c.label, i18n };
    }),
  };
}

/** A task type with every text resolved for `locale` (exact → language → English → first). */
export function resolveTaskType(type: RawTaskType, locale: string): TaskTypeDTO {
  return {
    ...type,
    name: resolveText(type.name, locale),
    description: resolveText(type.description, locale),
    fields: type.fields.map((f) => ({
      ...f,
      label: resolveText(f.label, locale),
      description: resolveText(f.description, locale),
      config: resolveOptionLabels(f.config, locale),
    })),
    states: type.states.map((x) => ({ ...x, label: resolveText(x.label, locale) })),
    outcomes: type.outcomes.map((x) => ({ ...x, label: resolveText(x.label, locale) })),
    playbook: {
      ...type.playbook,
      steps: type.playbook.steps.map((s) => ({
        ...s,
        name: resolveText(s.name, locale),
        description: resolveText(s.description, locale),
      })),
    },
  };
}

/**
 * A saved task type merged with what was there: a plain text that a German member writes sets only
 * the German value of a localized name or label (and changes nothing when it is what they read),
 * so the editor — which works on the reader's texts — never drops another language.
 */
export function mergeTaskType(current: RawTaskType | null, next: RawTaskType, locale: string): RawTaskType {
  if (!current) return next;
  const labels = new Map(current.fields.map((f) => [f.key, f.label]));
  const descriptions = new Map(current.fields.map((f) => [f.key, f.description]));
  const configs = new Map(current.fields.map((f) => [f.key, f.config]));
  const states = new Map(current.states.map((f) => [f.key, f.label]));
  const outcomes = new Map(current.outcomes.map((f) => [f.key, f.label]));
  const stepNames = new Map(current.playbook.steps.map((s) => [s.id, s]));
  return {
    ...next,
    name: nextText(current.name, next.name, locale),
    description: next.description === '' ? '' : nextText(current.description, next.description, locale),
    fields: next.fields.map((f) => ({
      ...f,
      label: nextText(labels.get(f.key), f.label, locale),
      description: f.description === '' ? '' : nextText(descriptions.get(f.key), f.description, locale),
      config: mergeOptions(configs.get(f.key), f.config, locale),
    })),
    states: next.states.map((f) => ({ ...f, label: nextText(states.get(f.key), f.label, locale) })),
    outcomes: next.outcomes.map((f) => ({ ...f, label: nextText(outcomes.get(f.key), f.label, locale) })),
    playbook: {
      ...next.playbook,
      steps: next.playbook.steps.map((s) => ({
        ...s,
        name: nextText(stepNames.get(s.id)?.name, s.name, locale),
        description:
          s.description === '' ? '' : nextText(stepNames.get(s.id)?.description, s.description, locale),
      })),
    },
    // The server owns what a type is bound to.
    system: current.system,
  };
}

const L = (en: string, de: string): LocalizedText => ({ en, de });
export const TaskRecurrence = z.object({
  frequency: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
  interval: z.number().int().min(1).max(365).default(1),
  until: z.iso.date().nullable().default(null),
});
export type TaskRecurrence = z.infer<typeof TaskRecurrence>;
export const TaskLink = z.object({ objectKey: z.string().min(1).max(63), recordId: z.uuid() });
/** Record references are preferred. Legacy name/email assignments remain readable. */
export const TaskParticipant = z.union([
  TaskLink,
  z.object({ name: z.string().trim().min(1).max(150), email: z.email().max(250) }),
]);
const taskProperties = {
  title: z.string().trim().min(1).max(250),
  typeKey: z.string().min(1).max(63).default('general'),
  description: BlockDocument.nullable().default(null),
  assigneeId: z.uuid().nullable().default(null),
  externalAssignee: TaskParticipant.nullable().default(null),
  supervisorId: z.uuid().nullable().default(null),
  dueOn: z.iso.date().nullable().default(null),
  dueAt: z.iso.datetime({ offset: true }).nullable().optional(),
  priority: z.enum(['low', 'normal', 'high', 'urgent']).default('normal'),
  links: z.array(TaskLink).max(20).default([]),
  fields: z.record(z.string(), z.unknown()).default({}),
  recurrence: TaskRecurrence.nullable().default(null),
  /** The task this one is a part of (subtask), and the tasks it waits for. */
  parentId: z.uuid().nullable().default(null),
  blockedBy: z.array(z.uuid()).max(20).default([]),
};
export const CreateTaskInput = z.object(taskProperties).strict();
export type CreateTaskInput = z.input<typeof CreateTaskInput>;
export const UpdateTaskInput = z
  .object({
    title: taskProperties.title.optional(),
    typeKey: taskProperties.typeKey.removeDefault().optional(),
    description: taskProperties.description.removeDefault().optional(),
    assigneeId: taskProperties.assigneeId.removeDefault().optional(),
    externalAssignee: taskProperties.externalAssignee.removeDefault().optional(),
    supervisorId: taskProperties.supervisorId.removeDefault().optional(),
    dueOn: taskProperties.dueOn.removeDefault().optional(),
    dueAt: taskProperties.dueAt,
    priority: taskProperties.priority.removeDefault().optional(),
    links: taskProperties.links.removeDefault().optional(),
    fields: taskProperties.fields.removeDefault().optional(),
    recurrence: taskProperties.recurrence.removeDefault().optional(),
    version: z.number().int().positive(),
    status: TaskStatus.optional(),
    result: z.string().max(20000).optional(),
    state: z.string().regex(KEY).max(40).nullable().optional(),
    outcome: z.string().regex(KEY).max(40).nullable().optional(),
    parentId: z.uuid().nullable().optional(),
    blockedBy: z.array(z.uuid()).max(20).optional(),
  })
  .strict();
export type UpdateTaskInput = z.input<typeof UpdateTaskInput>;
export const TASK_ORIGINS = [
  'playbook',
  'workflow',
  'request',
  'signature',
  'conflict',
  'deviation',
  'recurrence',
  'api',
] as const;
export type TaskOrigin = (typeof TASK_ORIGINS)[number];
/** Where the value of a task field came from (provenance): a person, an automation, an integration, the API, AI. */
export const TASK_FIELD_SOURCES = [
  'manual',
  'automation',
  'integration',
  'api',
  'import',
  'computed',
  'ai',
] as const;
export type TaskFieldSource = (typeof TASK_FIELD_SOURCES)[number];
export type TaskFieldMeta = {
  source: TaskFieldSource;
  /** The member who wrote it (null for the system and automations). */
  by: string | null;
  at: string;
  /** AI fields: how the value was generated (`AiFieldEvidence`: sources, explanation, model, mode). */
  evidence?: Record<string, unknown>;
  /** AI fields: the value before the last generation (revert). */
  previous?: unknown;
};
export type TaskSlaState = 'none' | 'ok' | 'warn' | 'breached' | 'met' | 'missed';
export type TaskDTO = Omit<z.infer<typeof CreateTaskInput>, 'externalAssignee'> & {
  externalAssignee: (z.infer<typeof TaskParticipant> & { name: string }) | null;
  id: string;
  status: z.infer<typeof TaskStatus>;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  result: string;
  artifactId: string | null;
  previousId: string | null;
  canEdit: boolean;
  requiresReview: boolean;
  /** Task platform: the type's own state (key, null = the category's default) and how the task ended. */
  state: string | null;
  stateLabel: string;
  outcome: string | null;
  outcomeLabel: string | null;
  origin: TaskOrigin | null;
  parentId: string | null;
  subtasks: { total: number; done: number };
  /** Open tasks this one waits for, and how many open tasks wait for this one. */
  blockedBy: string[];
  blocking: number;
  slaDueAt: string | null;
  sla: TaskSlaState;
  escalationLevel: number;
  /** People who may claim or act on the task while nobody is assigned (the type's pool, an origin's approvers). */
  eligible: string[];
  unclaimed: boolean;
  watching: boolean;
  /** Provenance per field key (who or what wrote the current value, AI evidence). */
  fieldMeta?: Record<string, TaskFieldMeta>;
  /** Next steps (task-actions): what still waits for a person after the outcome; absent when there are none. */
  nextSteps?: TaskNextSteps;
};
/** What a person does to a task in one step (`POST /tasks/:id/actions`). */
export const TaskActionInput = z.discriminatedUnion('action', [
  z.object({ action: z.literal('start'), version: z.number().int().positive() }).strict(),
  z
    .object({
      action: z.literal('submit'),
      version: z.number().int().positive(),
      note: z.string().max(20000).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('complete'),
      version: z.number().int().positive(),
      outcome: z.string().regex(KEY).max(40).optional(),
      note: z.string().max(20000).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('cancel'),
      version: z.number().int().positive(),
      outcome: z.string().regex(KEY).max(40).optional(),
      note: z.string().max(20000).optional(),
    })
    .strict(),
  z.object({ action: z.literal('reopen'), version: z.number().int().positive() }).strict(),
  z.object({ action: z.literal('claim'), version: z.number().int().positive() }).strict(),
  z.object({ action: z.literal('release'), version: z.number().int().positive() }).strict(),
  z
    .object({
      action: z.literal('delegate'),
      version: z.number().int().positive(),
      to: z.uuid(),
      note: z.string().max(2000).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('set-state'),
      version: z.number().int().positive(),
      state: z.string().regex(KEY).max(40),
    })
    .strict(),
  z.object({ action: z.literal('watch') }).strict(),
  z.object({ action: z.literal('unwatch') }).strict(),
]);
export type TaskActionInput = z.infer<typeof TaskActionInput>;
export const TaskListQuery = z.object({
  scope: z.enum(['all', 'mine', 'supervising', 'attention']).default('all'),
  status: z.union([TaskStatus, z.literal('active')]).optional(),
  typeKey: z.string().max(63).optional(),
  objectKey: z.string().max(63).optional(),
  recordId: z.uuid().optional(),
  origin: z.enum(TASK_ORIGINS).optional(),
  parentId: z.uuid().optional(),
  q: z.string().max(250).optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type PublicTaskDTO = Pick<
  TaskDTO,
  'id' | 'title' | 'dueOn' | 'dueAt' | 'status' | 'result' | 'version'
> & {
  action: TaskTypeDTO['action'];
  assigneeName: string;
  instructions: string;
  sender: string;
  workspaceName: string;
  locale: 'en' | 'de';
  timezone: string;
  brand?: {
    /** The kit's public key (`usePublicBrand`: the public-page theme's fonts). */
    key?: string;
    name: string;
    logoUrl: string | null;
    accent: string | null;
    /** The brand's square mark; when `logoUrl` is another image, it is a wordmark that names the workspace. */
    markUrl?: string | null;
    /** The logo for dark backgrounds, shown in dark mode. */
    logoOnDarkUrl?: string | null;
    /** The accent of app-like public surfaces (the kit's `app.accent`), else null. */
    appAccent?: string | null;
  } | null;
};
export type TaskFileDTO = { id: string; name: string; size: number; createdAt: string };
/** An empty type for the editor to start from (every default filled, texts plain). */
export function blankTaskType(): TaskTypeDTO {
  return resolveTaskType(TaskTypeInput.parse({ key: 'blank', name: 'blank' }), 'en');
}
/** Fill every default of a type written in short form. */
function typeOf(type: z.input<typeof TaskTypeInput>): RawTaskType {
  return TaskTypeInput.parse(type);
}
const outcome = (
  key: string,
  en: string,
  de: string,
  tone: RawTaskOutcomeDef['tone'],
  status: RawTaskOutcomeDef['status'],
  note: RawTaskOutcomeDef['note'] = 'none',
): RawTaskOutcomeDef => ({ key, label: L(en, de), tone, status, note });

/** The state of a template deviation: not "Waiting for decision", which names the approval of a workflow. */
export const DEVIATION_STATE_LABEL = L('Review the change', 'Änderung prüfen');
/** What deviation approvals were called before they had their own state name (stored types are renamed on read). */
export const DEVIATION_STATE_LABEL_BEFORE = L('Waiting for decision', 'Wartet auf Entscheidung');

/** The outcomes of a type that defines none: done, or cancelled. */
export const DEFAULT_TASK_OUTCOMES: RawTaskOutcomeDef[] = [
  outcome('done', 'Done', 'Erledigt', 'success', 'completed'),
  outcome('cancelled', 'Cancelled', 'Abgebrochen', 'neutral', 'cancelled'),
];
/** The states of a type that defines none: one per status category. */
export const DEFAULT_TASK_STATES: RawTaskStateDef[] = [
  { key: 'open', label: L('Open', 'Offen'), status: 'open' },
  { key: 'in_progress', label: L('In progress', 'In Bearbeitung'), status: 'in_progress' },
  { key: 'in_review', label: L('In review', 'In Prüfung'), status: 'in_review' },
  { key: 'completed', label: L('Done', 'Erledigt'), status: 'completed' },
  { key: 'cancelled', label: L('Cancelled', 'Abgebrochen'), status: 'cancelled' },
];

/**
 * Editable starting types, in every shipped language. Execution behavior is configured on each
 * workspace type.
 */
export const DEFAULT_TASK_TYPES: RawTaskType[] = [
  typeOf({
    key: 'general',
    name: L('Task', 'Aufgabe'),
    description: L('Any work to be done', 'Jede anstehende Arbeit'),
    action: 'none',
    actionField: null,
    fields: [],
    requiresReview: false,
    archived: false,
    icon: 'task',
    subtasks: { allow: true, requireDone: false },
  }),
  ...(
    [
      {
        key: 'email',
        name: L('Send an email', 'E-Mail senden'),
        action: 'email',
        actionField: 'recipients',
        completion: 'action',
        icon: 'mail',
        ui: 'email',
        fields: [
          {
            key: 'recipients',
            label: L('Recipients', 'Empfänger'),
            type: 'relation',
            objectTrait: 'person',
            multiple: true,
            required: true,
            options: [],
          },
        ],
      },
      {
        key: 'call',
        name: L('Make a call', 'Anrufen'),
        action: 'call',
        actionField: 'person',
        completion: 'action',
        icon: 'phone',
        ui: 'call',
        fields: [
          {
            key: 'person',
            label: L('Person to call', 'Anzurufende Person'),
            type: 'relation',
            objectTrait: 'person',
            multiple: false,
            required: true,
            options: [],
          },
        ],
      },
      {
        key: 'page',
        name: L('Write a note', 'Notiz schreiben'),
        action: 'page',
        actionField: null,
        icon: 'pencil',
        ui: 'document',
        fields: [],
      },
      {
        key: 'presentation',
        name: L('Create a presentation', 'Präsentation erstellen'),
        action: 'presentation',
        actionField: null,
        icon: 'gallery',
        ui: 'document',
        fields: [],
      },
      {
        key: 'pdf',
        name: L('Create a PDF', 'PDF erstellen'),
        action: 'pdf',
        actionField: null,
        icon: 'file',
        ui: 'document',
        fields: [],
      },
    ] as const
  ).map((type) =>
    typeOf({
      ...type,
      description: '',
      requiresReview: false,
      archived: false,
      fields: type.fields.map((field) => ({ ...field, options: [...field.options] })),
    }),
  ),
  typeOf({
    key: 'review',
    name: L('Review', 'Prüfung'),
    description: L(
      'Check work that someone submitted and approve it or send it back',
      'Eingereichte Arbeit prüfen und freigeben oder zurückgeben',
    ),
    ui: 'review',
    icon: 'search',
    color: 'violet',
    requiresReview: true,
    states: [
      { key: 'open', label: L('To review', 'Zu prüfen'), status: 'open' },
      { key: 'in_review', label: L('In review', 'In Prüfung'), status: 'in_review' },
      { key: 'sent_back', label: L('Sent back', 'Zurückgegeben'), status: 'in_progress' },
    ],
    outcomes: [
      outcome('approved', 'Approved', 'Freigegeben', 'success', 'completed'),
      outcome('rejected', 'Rejected', 'Abgelehnt', 'danger', 'completed', 'required'),
      outcome('cancelled', 'Cancelled', 'Abgebrochen', 'neutral', 'cancelled'),
    ],
  }),
  typeOf({
    key: 'approval',
    name: L('Approval', 'Freigabe'),
    description: L(
      'A decision an automation or a person asks for',
      'Eine Entscheidung, die eine Automatisierung oder eine Person erbittet',
    ),
    ui: 'approval',
    icon: 'shield',
    color: 'amber',
    system: true,
    manualCreation: false,
    states: [
      { key: 'pending', label: L('Waiting for decision', 'Wartet auf Entscheidung'), status: 'open' },
      {
        key: 'changes_requested',
        label: L('Changes requested', 'Änderungen angefragt'),
        status: 'in_progress',
      },
    ],
    outcomes: [
      outcome('approved', 'Approved', 'Freigegeben', 'success', 'completed'),
      outcome('rejected', 'Rejected', 'Abgelehnt', 'danger', 'completed', 'optional'),
      outcome('cancelled', 'Cancelled', 'Abgebrochen', 'neutral', 'cancelled'),
    ],
  }),
  typeOf({
    key: 'sign_document',
    name: L('Sign document', 'Dokument unterschreiben'),
    description: L(
      'A document that waits for your signature',
      'Ein Dokument, das auf Ihre Unterschrift wartet',
    ),
    ui: 'sign_document',
    icon: 'pencil',
    color: 'indigo',
    system: true,
    manualCreation: false,
    states: [{ key: 'to_sign', label: L('To sign', 'Zu unterschreiben'), status: 'open' }],
    outcomes: [
      outcome('signed', 'Signed', 'Unterschrieben', 'success', 'completed'),
      outcome('declined', 'Declined', 'Abgelehnt', 'danger', 'completed', 'optional'),
      outcome('cancelled', 'Withdrawn', 'Zurückgezogen', 'neutral', 'cancelled'),
      outcome('expired', 'Expired', 'Abgelaufen', 'warning', 'cancelled'),
    ],
  }),
  typeOf({
    key: 'collect_signatures',
    name: L('Collect signatures', 'Unterschriften einholen'),
    description: L(
      'A document you sent waits for signers outside the workspace: remind them or withdraw it',
      'Ein von Ihnen versendetes Dokument wartet auf Unterzeichner außerhalb des Workspaces: erinnern oder zurückziehen',
    ),
    ui: 'collect_signatures',
    icon: 'pencil',
    color: 'indigo',
    system: true,
    manualCreation: false,
    states: [{ key: 'waiting', label: L('Waiting for signers', 'Wartet auf Unterzeichner'), status: 'open' }],
    outcomes: [
      outcome('signed', 'Signed', 'Unterschrieben', 'success', 'completed'),
      outcome('declined', 'Declined', 'Abgelehnt', 'danger', 'completed', 'optional'),
      outcome('cancelled', 'Withdrawn', 'Zurückgezogen', 'neutral', 'cancelled'),
      outcome('expired', 'Expired', 'Abgelaufen', 'warning', 'cancelled'),
    ],
  }),
  typeOf({
    key: 'data_conflict',
    name: L('Resolve data conflicts', 'Datenkonflikte klären'),
    description: L(
      'Sources disagree about a value: choose which one to keep',
      'Quellen widersprechen sich bei einem Wert: Wählen Sie, welcher gilt',
    ),
    ui: 'data_conflict',
    icon: 'branch',
    color: 'rose',
    system: true,
    manualCreation: false,
    assignment: {
      mode: 'pool',
      memberId: null,
      pool: { memberIds: [], roleKeys: ['admin'], teamIds: [] },
      strategy: 'claim',
    },
    states: [{ key: 'to_resolve', label: L('To resolve', 'Zu klären'), status: 'open' }],
    outcomes: [
      outcome('resolved', 'Resolved', 'Geklärt', 'success', 'completed'),
      outcome('cancelled', 'Cancelled', 'Abgebrochen', 'neutral', 'cancelled'),
    ],
  }),
  typeOf({
    key: 'deviation_approval',
    name: L('Approve document change', 'Dokumentänderung freigeben'),
    description: L(
      'A record’s document differs from its template in a protected clause: approve or reject the change',
      'Das Dokument eines Datensatzes weicht in einer geschützten Klausel von der Vorlage ab: Änderung freigeben oder ablehnen',
    ),
    ui: 'deviation_approval',
    icon: 'shield',
    color: 'amber',
    system: true,
    manualCreation: false,
    states: [{ key: 'to_approve', label: DEVIATION_STATE_LABEL, status: 'open' }],
    outcomes: [
      outcome('approved', 'Approved', 'Freigegeben', 'success', 'completed'),
      outcome('rejected', 'Rejected', 'Abgelehnt', 'danger', 'completed', 'optional'),
      outcome('cancelled', 'Withdrawn', 'Zurückgezogen', 'neutral', 'cancelled'),
    ],
  }),
];

/** The specialized UI of a type: its own, else the one its action implies (stored older types), else `generic`. */
export function effectiveTaskUi(type: Pick<TaskTypeDTO, 'ui' | 'action'>): string {
  if (type.ui !== 'generic') return type.ui;
  return type.action === 'email'
    ? 'email'
    : type.action === 'call'
      ? 'call'
      : ['page', 'presentation', 'pdf'].includes(type.action)
        ? 'document'
        : 'generic';
}

/** The type's states, or the five defaults (their labels in `locale`). */
export function effectiveStates<S extends { key: string; status: TaskStatus; label: unknown }>(
  type: { states: S[] },
  locale = 'en',
): S[] {
  if (type.states.length) return type.states;
  return DEFAULT_TASK_STATES.map((s) => ({ ...s, label: resolveText(s.label, locale) })) as unknown as S[];
}
/** The type's outcomes, or done/cancelled (their labels in `locale`). */
export function effectiveOutcomes<O extends { key: string; label: unknown }>(
  type: { outcomes: O[] },
  locale = 'en',
): O[] {
  if (type.outcomes.length) return type.outcomes;
  return DEFAULT_TASK_OUTCOMES.map((o) => ({ ...o, label: resolveText(o.label, locale) })) as unknown as O[];
}
/** The outcomes that end a task in `status`. */
export function outcomesFor<O extends { key: string; label: unknown; status: 'completed' | 'cancelled' }>(
  type: { outcomes: O[] },
  status: 'completed' | 'cancelled',
  locale = 'en',
): O[] {
  return effectiveOutcomes(type, locale).filter((o) => o.status === status);
}
/** The state a task is in: its own, else the first of its category. */
export function stateOf<S extends { key: string; status: TaskStatus }>(
  states: S[],
  task: { state: string | null; status: TaskStatus },
): S | undefined {
  return (
    states.find((x) => x.key === task.state && x.status === task.status) ??
    states.find((x) => x.status === task.status)
  );
}

/** How an open task stands against its SLA, and how a closed one ended. */
export function taskSlaState(input: {
  status: TaskStatus;
  createdAt: string;
  slaDueAt: string | null;
  completedAt: string | null;
  warnAt: number | null;
  now: number;
}): TaskSlaState {
  if (!input.slaDueAt) return 'none';
  const due = Date.parse(input.slaDueAt);
  if (isClosedTask(input.status))
    return input.completedAt && Date.parse(input.completedAt) > due ? 'missed' : 'met';
  if (input.now > due) return 'breached';
  if (input.warnAt) {
    const start = Date.parse(input.createdAt);
    if (due > start && (input.now - start) / (due - start) >= input.warnAt / 100) return 'warn';
  }
  return 'ok';
}

const HOUR = 3_600_000;
/** `from` plus a duration; business days skip Saturday and Sunday of the time zone. */
export function addTaskDuration(from: Date, d: TaskDuration, timeZone: string): Date {
  if (d.unit === 'hours') return new Date(from.getTime() + d.amount * HOUR);
  if (d.unit === 'days') return new Date(from.getTime() + d.amount * 24 * HOUR);
  const weekend = (at: Date) => {
    const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone }).format(at);
    return day === 'Sat' || day === 'Sun';
  };
  let at = new Date(from.getTime());
  for (let left = d.amount; left > 0;) {
    at = new Date(at.getTime() + 24 * HOUR);
    if (!weekend(at)) left--;
  }
  return at;
}

/** The settings of a `record_ref` field: which records it may hold and whether it holds several. */
export function taskRefSettings(field: { config: Record<string, unknown> }): {
  objectKey: string | undefined;
  trait: 'person' | undefined;
  multiple: boolean;
} {
  const c = field.config;
  return {
    objectKey: typeof c.objectKey === 'string' && c.objectKey ? c.objectKey : undefined,
    trait: c.trait === 'person' ? 'person' : undefined,
    multiple: c.multiple === true,
  };
}
/** The types whose values the engine computes (nobody writes them). */
export const TASK_COMPUTED_FIELD_TYPES: readonly string[] = ['formula', 'rollup', 'lookup', 'ai_field'];
export const isComputedTaskField = (field: { type: string }) =>
  TASK_COMPUTED_FIELD_TYPES.includes(field.type);

/**
 * A stored default type still exactly as an older version created it (plain English texts): it
 * reads in every shipped language now. A type the workspace changed is left alone.
 */
export function localizeDefaultTaskType(type: RawTaskType): RawTaskType {
  const fresh = DEFAULT_TASK_TYPES.find((d) => d.key === type.key);
  if (!fresh) return type;
  const english = (x: LocalizedText) => (typeof x === 'string' ? x : null);
  const enOf = (x: LocalizedText) => resolveText(x, 'en');
  const same =
    english(type.name) === enOf(fresh.name) &&
    (type.description === '' || english(type.description) === enOf(fresh.description));
  if (!same) return type;
  const labels = new Map(fresh.fields.map((f) => [f.key, f.label]));
  return {
    ...type,
    name: fresh.name,
    description: type.description === '' ? '' : fresh.description,
    fields: type.fields.map((f) => {
      const label = labels.get(f.key);
      return label && english(f.label) === enOf(label) ? { ...f, label } : f;
    }),
  };
}

/** Calendar recurrence in date-only space: clamp month ends and skip missed periods. */
export function nextTaskDue(dueOn: string, recurrence: TaskRecurrence, after: string): string | null {
  const anchor = new Date(`${dueOn}T12:00:00Z`);
  for (let n = 1; n <= 200000; n++) {
    const date = new Date(anchor),
      amount = n * recurrence.interval;
    if (recurrence.frequency === 'daily' || recurrence.frequency === 'weekly')
      date.setUTCDate(date.getUTCDate() + amount * (recurrence.frequency === 'weekly' ? 7 : 1));
    else {
      date.setUTCDate(1);
      date.setUTCMonth(date.getUTCMonth() + amount * (recurrence.frequency === 'yearly' ? 12 : 1));
      const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
      date.setUTCDate(Math.min(anchor.getUTCDate(), last));
    }
    const next = date.toISOString().slice(0, 10);
    if (recurrence.until && next > recurrence.until) return null;
    if (next > after) return next;
  }
  return null;
}

/** Upgrade legacy contact-field configuration without discarding previously stored addresses. */
export function personActionType(type: RawTaskType): RawTaskType {
  if (!['email', 'call'].includes(type.action)) return type;
  return {
    ...type,
    fields: type.fields.map((field) =>
      field.key === type.actionField && ['email', 'phone'].includes(field.type)
        ? {
            ...field,
            type: 'record_ref',
            config: { trait: 'person', multiple: type.action === 'email' },
            label:
              type.action === 'email'
                ? L('Recipients', 'Empfänger')
                : L('Person to call', 'Anzurufende Person'),
          }
        : field,
    ),
  };
}
export function taskPersonRefs(
  value: unknown,
): Array<{ objectKey: string; recordId: string; title?: string }> {
  return (Array.isArray(value) ? value : value ? [value] : []).flatMap((v) => {
    const parsed = TaskLink.safeParse(v);
    return parsed.success
      ? [
          {
            ...parsed.data,
            ...(typeof v === 'object' && v !== null && 'title' in v && typeof v.title === 'string'
              ? { title: v.title }
              : {}),
          },
        ]
      : [];
  });
}
